import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import AuthLayout from '@/components/AuthLayout';
import { useAuthContext } from '@/features/auth/AuthProvider';
import { extractError } from '@/lib/axios';
import { config } from '@/config/env';

interface FormValues {
  email: string;
  password: string;
  mfaToken?: string;
}

export default function LoginPage(): JSX.Element {
  const { login } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [mfaRequired, setMfaRequired] = useState(false);
  const { register, handleSubmit, formState } = useForm<FormValues>();
  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login(values);
      navigate(from, { replace: true });
    } catch (err) {
      const e = extractError(err);
      if (e.message === 'MFA_REQUIRED') {
        setMfaRequired(true);
        toast('Enter your authenticator code');
        return;
      }
      toast.error(e.message);
    }
  });

  return (
    <AuthLayout title="Welcome back" subtitle={<>Log in to your {config.appName} account</>}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium">Email</label>
          <input className="input" type="email" autoComplete="email" {...register('email', { required: true })} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Password</label>
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            {...register('password', { required: true })}
          />
        </div>
        {mfaRequired && (
          <div>
            <label className="mb-1 block text-sm font-medium">Authenticator code</label>
            <input className="input" inputMode="numeric" placeholder="123456" {...register('mfaToken')} />
          </div>
        )}
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-sm text-brand-600 hover:underline">
            Forgot password?
          </Link>
        </div>
        <button type="submit" className="btn-primary w-full" disabled={formState.isSubmitting}>
          {formState.isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <a href={`${config.apiUrl}/auth/google`} className="btn-ghost mt-3 w-full border border-slate-300 dark:border-slate-700">
        Continue with Google
      </a>

      <p className="mt-6 text-center text-sm text-slate-600 dark:text-slate-400">
        No account?{' '}
        <Link to="/register" className="text-brand-600 hover:underline">
          Sign up
        </Link>
      </p>
    </AuthLayout>
  );
}
